import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { toast } from "react-toastify";

import AssignmentsSection from "./AssignmentsSection";
import HoursSection from "./HoursSection";
import MeetingsSection from "./MeetingsSection";
import PaymentsSection from "./PaymentsSection";
import ProgressSection from "./ProgressSection";
import { PageHeading, PortalContainer, PortalLoading, SummaryCards } from "./PortalShared";
import { apiError, formatDate, formatHours, superviseeApi } from "./portalApi";
import { SuperviseeLoggingBanner } from "./PlanNotices";

/**
 * A supervisee's own dashboard.
 *
 * Every call from this screen goes to `/supervision/me/...`, which takes no
 * id — there is no way from here to name another supervisee, and the server
 * resolves the record set from the session regardless of what is sent.
 */
export default function SuperviseeDashboard() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const loadDashboard = React.useCallback(async () => {
    try {
      const res = await superviseeApi.dashboard();
      setData(res.data);
    } catch (err) {
      toast.error(apiError(err, "Could not load your supervision record"));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Stable adapters, or HoursSection's effect refires on every render.
  const hoursApi = React.useMemo(
    () => ({
      list: superviseeApi.listHours,
      add: superviseeApi.addHours,
      update: superviseeApi.updateHours,
      remove: superviseeApi.deleteHours,
    }),
    [],
  );

  const meetingsApi = React.useMemo(() => ({ list: superviseeApi.listMeetings }), []);

  const progressApi = React.useMemo(() => ({ load: superviseeApi.progress }), []);

  const assignmentsApi = React.useMemo(
    () => ({
      list: superviseeApi.listAssignments,
      update: superviseeApi.respondToAssignment,
      fileUrl: superviseeApi.attachmentUrl,
    }),
    [],
  );

  const paymentsApi = React.useMemo(() => ({ list: superviseeApi.listPayments }), []);

  if (loading) return <PortalLoading />;
  if (!data) return null;

  const { profile, summary } = data;
  // Closed while archived or while the supervisor's plan has ended; the
  // server says which (and refuses the writes either way).
  const loggingOpen = data.logging?.open !== false;
  const repliesNote =
    data.logging?.reason === "supervisor_plan_ended"
      ? "Replies to assignments are paused until your supervisor renews their plan."
      : data.logging?.reason === "archived"
        ? "Replies to assignments are closed while your record is archived."
        : null;

  const handleExport = async () => {
    try {
      await superviseeApi.exportRecords();
    } catch (err) {
      toast.error(apiError(err, "Could not download your records"));
    }
  };

  return (
    <PortalContainer>
      <PageHeading
        title="My supervision"
        subtitle={`${profile.board?.name || "No board"} · started ${formatDate(
          profile.supervisionStartDate,
        )}`}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            {profile.status !== "active" && (
              <Chip
                label={profile.status === "paused" ? "Paused" : "Completed"}
                color={profile.status === "paused" ? "warning" : "success"}
              />
            )}
            <Button variant="outlined" onClick={handleExport}>
              Download my records (CSV)
            </Button>
          </Stack>
        }
      />

      <SuperviseeLoggingBanner logging={data.logging} />

      <SummaryCards summary={summary} />
      {/* refreshKey: a new dashboard payload after any edit redraws the charts. */}
      <ProgressSection progressApi={progressApi} refreshKey={data} />

      <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          My details
        </Typography>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
          }}
        >
          <Detail label="Certifying board" value={profile.board?.fullName || profile.board?.name} />
          <Detail label="Required total hours" value={formatHours(profile.requiredTotalHours)} />
          <Detail label="Supervision ratio" value={profile.supervisionRatio || "—"} />
          <Detail label="Supervision started" value={formatDate(profile.supervisionStartDate)} />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
          Your board and required hours are set by your supervisor. Ask her if
          anything here looks wrong.
        </Typography>
      </Card>

      <HoursSection hoursApi={hoursApi} canEdit={loggingOpen} onChanged={loadDashboard} />
      <MeetingsSection meetingsApi={meetingsApi} canEdit={false} />
      {/* Hidden until the supervisor sets one — many never will. */}
      <AssignmentsSection
        assignmentsApi={assignmentsApi}
        mode="supervisee"
        hideWhenEmpty
        canEdit={loggingOpen}
        note={repliesNote}
        onChanged={loadDashboard}
      />
      {/* Hidden until the supervisor records a payment. */}
      <PaymentsSection paymentsApi={paymentsApi} mode="supervisee" />
    </PortalContainer>
  );
}

function Detail({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {value || "—"}
      </Typography>
    </Box>
  );
}
